import {
  BoatingRiskRating,
  WaterBody,
  classifyHour,
  type HourlyMarineConditions,
  type VesselProfile,
} from '@deckee/utilities';
import { DateTime } from 'luxon';
import { isIsoTimeInForecastDayChartDomain } from '../day/chartDomain';

export type WeatherForecastRiskLevel =
  | 'unknown'
  | 'caution'
  | 'warning'
  | 'danger';

export interface WeatherGraphPoint {
  x: string;
  y: number;
  /** Bearing the flow comes FROM, degrees. Present on wind and swell series. */
  direction?: number;
}

export interface WeatherRiskPoint {
  x: string;
  riskLevel: WeatherForecastRiskLevel;
}

export interface WeatherRiskGraphPoint {
  x: string;
  y: number;
  wind: number | null;
  swell: number | null;
  swellPeriod: number | null;
  riskLevel: WeatherForecastRiskLevel;
  /** The inputs the rating was made from, for copy and the animation. */
  conditions: HourlyMarineConditions;
}

/** The three engine ratings map onto the app's existing colour levels. */
const RISK_LEVEL_BY_RATING: Readonly<
  Record<BoatingRiskRating, WeatherForecastRiskLevel>
> = {
  [BoatingRiskRating.Green]: 'caution',
  [BoatingRiskRating.Amber]: 'warning',
  [BoatingRiskRating.Red]: 'danger',
};

export const riskLevelForRating = (
  rating: BoatingRiskRating,
): WeatherForecastRiskLevel => RISK_LEVEL_BY_RATING[rating];

export const isDangerRiskLevel = (
  riskLevel: WeatherForecastRiskLevel | undefined,
): boolean => riskLevel === 'danger';

const RISK_SEVERITY: Record<WeatherForecastRiskLevel, number> = {
  unknown: 0,
  caution: 1,
  warning: 2,
  danger: 3,
};

/** Day-strip first bar: 5am–12pm in the forecast location timezone. */
const DAY_STRIP_FIRST_PERIOD_START_HOUR = 5;
const DAY_STRIP_FIRST_PERIOD_END_HOUR = 12;
/** Day-strip second bar: 12pm–7pm in the forecast location timezone. */
const DAY_STRIP_SECOND_PERIOD_START_HOUR = 12;
const DAY_STRIP_SECOND_PERIOD_END_HOUR = 19;

export const worstRiskLevel = (
  riskLevels: WeatherForecastRiskLevel[],
): WeatherForecastRiskLevel =>
  riskLevels.reduce(
    (worst, current) =>
      RISK_SEVERITY[current] > RISK_SEVERITY[worst] ? current : worst,
    'unknown',
  );

const findPointForTimestamp = (
  windPoint: WeatherGraphPoint,
  points: WeatherGraphPoint[],
): WeatherGraphPoint | undefined => {
  const windTime = DateTime.fromISO(windPoint.x);

  if (!windTime.isValid) {
    return undefined;
  }

  return points.find((point) => {
    const pointTime = DateTime.fromISO(point.x);

    return pointTime.isValid && windTime.toMillis() === pointTime.toMillis();
  });
};

export interface BoatingForecastSeries {
  wind: WeatherGraphPoint[];
  swellHeight?: WeatherGraphPoint[];
  swellPeriod?: WeatherGraphPoint[];
}

/**
 * Join the hourly series into one rated timeline.
 *
 * The wind series is the spine: every hour with a wind reading produces a
 * rating, and swell height, period and direction are matched onto it by
 * timestamp. An hour missing a swell reading is rated as though the swell were
 * flat rather than dropped, so the band never has holes in it.
 */
export const buildBoatingForecastPoints = (
  vessel: VesselProfile,
  waterBody: WaterBody,
  series: BoatingForecastSeries,
): WeatherRiskGraphPoint[] =>
  series.wind.map((windPoint) => {
    const swellHeightPoint = findPointForTimestamp(
      windPoint,
      series.swellHeight ?? [],
    );
    const swellPeriodPoint = findPointForTimestamp(
      windPoint,
      series.swellPeriod ?? [],
    );

    const conditions: HourlyMarineConditions = {
      swellHeightM: swellHeightPoint?.y ?? 0,
      swellPeriodS: swellPeriodPoint?.y ?? 0,
      windSpeedMps: windPoint.y,
      windDirectionDeg: windPoint.direction ?? 0,
      // With no swell bearing there is nothing to oppose, so matching the wind
      // bearing makes the pair a following sea rather than inventing conflict.
      swellDirectionDeg:
        swellHeightPoint?.direction ?? windPoint.direction ?? 0,
    };

    return {
      y: 3,
      x: windPoint.x,
      wind: windPoint.y,
      swell: swellHeightPoint?.y ?? null,
      swellPeriod: swellPeriodPoint?.y ?? null,
      conditions,
      riskLevel: riskLevelForRating(
        classifyHour(conditions, vessel, waterBody),
      ),
    };
  });

export const buildBoatingForecastRiskPoints = (
  vessel: VesselProfile,
  waterBody: WaterBody,
  series: BoatingForecastSeries,
): WeatherRiskPoint[] =>
  buildBoatingForecastPoints(vessel, waterBody, series).map(
    ({ x, riskLevel }) => ({ x, riskLevel }),
  );

export const filterGraphPointsForDate = (
  points: WeatherGraphPoint[],
  date: string,
  timeZone: string,
): WeatherGraphPoint[] =>
  points.filter((point) =>
    isIsoTimeInForecastDayChartDomain(point.x, date, timeZone),
  );

const isFirstDayStripPeriodHour = (hour: number) =>
  hour >= DAY_STRIP_FIRST_PERIOD_START_HOUR &&
  hour < DAY_STRIP_FIRST_PERIOD_END_HOUR;

const isSecondDayStripPeriodHour = (hour: number) =>
  hour >= DAY_STRIP_SECOND_PERIOD_START_HOUR &&
  hour < DAY_STRIP_SECOND_PERIOD_END_HOUR;

export const getMorningAndNightRiskLevelsForDate = (
  riskPoints: WeatherRiskPoint[],
  date: string,
  timeZone: string,
): {
  morningRiskLevel: WeatherForecastRiskLevel;
  nightRiskLevel: WeatherForecastRiskLevel;
} => {
  const morningRiskLevels: WeatherForecastRiskLevel[] = [];
  const nightRiskLevels: WeatherForecastRiskLevel[] = [];

  riskPoints.forEach((riskPoint) => {
    const pointDateTime = DateTime.fromISO(riskPoint.x, { zone: timeZone });

    if (!pointDateTime.isValid || pointDateTime.toISODate() !== date) {
      return;
    }

    if (isFirstDayStripPeriodHour(pointDateTime.hour)) {
      morningRiskLevels.push(riskPoint.riskLevel);
    }

    if (isSecondDayStripPeriodHour(pointDateTime.hour)) {
      nightRiskLevels.push(riskPoint.riskLevel);
    }
  });

  return {
    morningRiskLevel: worstRiskLevel(morningRiskLevels),
    nightRiskLevel: worstRiskLevel(nightRiskLevels),
  };
};
